/**
 * Item math and generation (PLAN §11 "Gear"). Deterministic: all randomness from a passed Rng,
 * and the ORDER of draws in `generateItem` is documented there (loot replays depend on it).
 */
import type { ClassId, Rng, Role } from '@keystone/sim';
import { CLASS_STAT_PROFILES, MAX_GEM_RANK } from './data';
import { LEGENDARY_SOCKETS } from './items.legendaries';
import { trinketsForRole } from './items.trinkets';
import type { EquippedGear, Item, ItemId, ItemSlot, Rarity, SecondaryFractions, SecondaryStat } from './types';
import { ITEM_SLOTS, RARITIES, SECONDARY_STATS } from './types';
import { EMPOWER_MULT, TIER_PIECE_SCORE, TIER_SET_IDS } from './forge.constants';

export const BASE_ILVL = 200;
export const MAX_ILVL = 270;
/** Effective ilvl of an empty slot when averaging. */
export const EMPTY_SLOT_ILVL = 180;
/**
 * power(ilvl) = POWER_PER_ILVL^(ilvl − 200). PLAN §11 says 1.0105 (≈2.08× at 270); the harness
 * sweep (`pnpm harness -- sweep`) tuned it to 1.008 (≈1.75× at 270) so walls hit the §11 targets
 * under the sim's CURRENT keystone scaling (1.05/level to +10, then 1.10). If the sim moves to
 * PLAN §6's 1.08/level, 1.0105 fits again (`sweep --key-scaling 1.08`); re-run the sweep.
 */
export const POWER_PER_ILVL = 1.008;
/**
 * +econ-pacing: above POWER_LATE_FROM each item level is worth POWER_PER_ILVL_LATE instead (PLAN §11's
 * steeper 1.0105 where keystone scaling is 1.10/level). With 1.008 all the way the ilvl-270 wall sat
 * at +15 (target +16–18) while 200/240 were on target; the late slope lifts 255/270 only.
 */
export const POWER_LATE_FROM = 240;
export const POWER_PER_ILVL_LATE = 1.0115;
/** Rating per secondary per item at ilvl 200, slot weight 1, rare (scaled by powerMult). */
export const SECONDARY_RATING_AT_BASE = 20;
/** Rating needed for +1% (0.01) of a secondary. */
export const RATING_PER_PERCENT = 20;

/**
 * THE item-level power curve: main-stat multiplier for an item level. It scales power
 * (damage and healing) and max HP. 270 ≈ 1.75× a fresh character (see POWER_PER_ILVL). This is the one
 * function to tune; everything else (secondary budgets, compile's stats) derives from it.
 */
export function powerMult(ilvl: number): number {
  // +econ-pacing: the curve steepens past POWER_LATE_FROM (Paragon/Eternal gear), see POWER_PER_ILVL_LATE.
  const early = Math.min(ilvl, POWER_LATE_FROM) - BASE_ILVL;
  const late = Math.max(0, ilvl - POWER_LATE_FROM);
  return Math.pow(POWER_PER_ILVL, early) * Math.pow(POWER_PER_ILVL_LATE, late);
}
/** Alias with the PLAN's name. */
export const ilvlPower = powerMult;

/** Secondary budget weight per slot (big pieces carry more stats). */
export const SLOT_BUDGET: Readonly<Record<ItemSlot, number>> = {
  weapon: 1.3,
  helm: 1.0,
  chest: 1.15,
  legs: 1.15,
  gloves: 0.9,
  boots: 0.9,
  ring: 0.85,
  trinket: 0.75,
};

/** Secondary budget multiplier per rarity. */
export const RARITY_BUDGET: Readonly<Record<Rarity, number>> = { common: 0.9, uncommon: 0.95, rare: 1, epic: 1.1, legendary: 1.15 };

/** Rating of one secondary on an item of this ilvl/slot/rarity. */
export function secondaryBudget(ilvl: number, slot?: ItemSlot, rarity: Rarity = 'rare'): number {
  const w = slot ? SLOT_BUDGET[slot] : 1;
  return Math.round(SECONDARY_RATING_AT_BASE * powerMult(ilvl) * w * RARITY_BUDGET[rarity]);
}

/** Most masterwork steps an item can take (+1 effective ilvl each; upgrade.ts sells them). */
export const MASTERWORK_MAX = 3;

/** Item level including masterwork polish (what power and item score use). */
export function effectiveIlvl(item: Pick<Item, 'ilvl' | 'masterwork'>): number {
  return item.ilvl + (item.masterwork ?? 0);
}

/** Average equipped effective item level (masterwork included; empty slots count as EMPTY_SLOT_ILVL). */
export function averageIlvl(gear: EquippedGear): number {
  let sum = 0;
  for (const s of ITEM_SLOTS) sum += gear[s] ? effectiveIlvl(gear[s]!) : EMPTY_SLOT_ILVL;
  return sum / ITEM_SLOTS.length;
}

/** Secondary totals over gear, converted to fractions. */
export function secondaryFractions(gear: EquippedGear): SecondaryFractions {
  const out: SecondaryFractions = { haste: 0, crit: 0, expertise: 0, spirit: 0 };
  for (const s of ITEM_SLOTS) for (const r of gear[s]?.secondaries ?? []) out[r.stat] += r.rating / RATING_PER_PERCENT / 100;
  return out;
}

/**
 * Rating of secondary `index` of `item` at `ilvl`: budget × quality (default 1) × Empowered
 * (+15% from an Awakening). Rarity comes from the item.
 */
export function secondaryRating(item: Pick<Item, 'slot' | 'rarity' | 'secondaries' | 'forge'>, index: number, ilvl: number, rarity: Rarity = item.rarity): number {
  const q = item.secondaries[index]?.quality ?? 1;
  const emp = item.forge?.empowered?.includes(index) ? EMPOWER_MULT : 1;
  return Math.round(secondaryBudget(ilvl, item.slot, rarity) * q * emp);
}

/** Secondary ratings for a (possibly upgraded) ilvl and rarity, keeping stats, quality and Empowered. */
export function rescaleSecondaries(item: Item, ilvl: number, rarity: Rarity = item.rarity): Item['secondaries'] {
  return item.secondaries.map((r, i) => ({ ...r, rating: secondaryRating(item, i, ilvl, rarity) }));
}

// ============================================================================
// Rolling
// ============================================================================

/** Roll weight of a secondary by its position in the class priority (best first). */
export const PRIORITY_ROLL_WEIGHTS: readonly number[] = [4, 3, 2, 1];

export function statWeight(stat: SecondaryStat, priority: readonly SecondaryStat[] = SECONDARY_STATS): number {
  const i = priority.indexOf(stat);
  return i < 0 ? 1 : (PRIORITY_ROLL_WEIGHTS[i] ?? 1);
}

/** Two distinct secondaries, weighted toward the class priority. 2 draws. */
export function rollSecondaries(rng: Rng, priority: readonly SecondaryStat[] = SECONDARY_STATS): [SecondaryStat, SecondaryStat] {
  const pickOne = (pool: readonly SecondaryStat[]): SecondaryStat => {
    const total = pool.reduce((t, s) => t + statWeight(s, priority), 0);
    let r = rng.next() * total;
    for (const s of pool) {
      r -= statWeight(s, priority);
      if (r < 0) return s;
    }
    return pool[pool.length - 1]!;
  };
  const a = pickOne(SECONDARY_STATS);
  const b = pickOne(SECONDARY_STATS.filter((s) => s !== a));
  return [a, b];
}

/** Chance of at least one socket by slot (PLAN: "some items, 1–2 sockets"). */
export const SOCKET_CHANCE: Readonly<Record<ItemSlot, number>> = {
  weapon: 0.4,
  helm: 0.25,
  chest: 0.25,
  legs: 0.2,
  gloves: 0.2,
  boots: 0.2,
  ring: 0.5,
  trinket: 0.15,
};
/** Chance a socketed item has a second socket. Epic items add EPIC_SOCKET_BONUS to both rolls. */
export const SECOND_SOCKET_CHANCE = 0.25;
export const EPIC_SOCKET_BONUS = 0.15;
export const MAX_SOCKETS = 2;

/** Socket count roll: 1 draw, plus 1 more if the first succeeds. */
export function rollSockets(rng: Rng, slot: ItemSlot, rarity: Rarity = 'rare'): number {
  const bonus = rarity === 'epic' ? EPIC_SOCKET_BONUS : 0;
  if (!rng.chance(Math.min(1, SOCKET_CHANCE[slot] + bonus))) return 0;
  return rng.chance(Math.min(1, SECOND_SOCKET_CHANCE + bonus)) ? 2 : 1;
}

/** Epic chance rises with item level: 10% at 200, +1% per ilvl, capped at 60%. */
export function epicChance(ilvl: number): number {
  return Math.min(0.6, 0.1 + 0.01 * Math.max(0, ilvl - BASE_ILVL));
}

/** Chance a trinket-slot item carries an on-use/proc effect ("most have one"). */
export const TRINKET_EFFECT_CHANCE = 0.85;

export interface ItemSpec {
  id: ItemId;
  slot: ItemSlot;
  ilvl: number;
  rarity?: Rarity;
  /** Force these two stats (otherwise rolled, weighted toward `priority`). */
  stats?: [SecondaryStat, SecondaryStat];
  /** Secondary priority of the recipient (roll weights PRIORITY_ROLL_WEIGHTS). */
  priority?: readonly SecondaryStat[];
  sockets?: number;
  legendaryId?: string;
  trinketEffectId?: string;
  source?: Item['source'];
}

/** Build an item from a spec; rolls only what the spec leaves open (stats, then sockets). */
export function makeItem(spec: ItemSpec, rng: Rng): Item {
  const rarity = spec.rarity ?? (spec.legendaryId ? 'legendary' : 'rare');
  const stats = spec.stats ?? rollSecondaries(rng, spec.priority);
  const sockets = Math.min(MAX_SOCKETS, spec.sockets ?? (spec.legendaryId ? LEGENDARY_SOCKETS : rollSockets(rng, spec.slot, rarity)));
  const budget = secondaryBudget(spec.ilvl, spec.slot, rarity);
  const item: Item = {
    id: spec.id,
    slot: spec.slot,
    ilvl: spec.ilvl,
    rarity,
    secondaries: stats.map((stat) => ({ stat, rating: budget })),
    sockets,
    gems: Array.from({ length: sockets }, () => null),
  };
  if (spec.legendaryId) item.legendaryId = spec.legendaryId;
  if (spec.trinketEffectId) item.trinketEffectId = spec.trinketEffectId;
  if (spec.source) item.source = spec.source;
  return item;
}

export interface GenerateSpec {
  id: ItemId;
  ilvl: number;
  /** Recipient class: drives the stat priority. */
  classId?: ClassId;
  /** Recipient role: limits which trinket effects can roll. */
  role?: Role;
  /** Force a slot (otherwise uniform over the 8 slots). */
  slot?: ItemSlot;
  /** Forbid these slots when rolling one (vault choices use it to offer 3 different slots). */
  excludeSlots?: readonly ItemSlot[];
  source?: Item['source'];
}

/**
 * Generate a non-legendary item. RNG draw order (part of the save/replay contract):
 *  1. slot (unless forced)  2. rarity  3. trinket effect: chance, then pick (trinket slot only)
 *  4. two secondaries        5. sockets
 */
export function generateItem(spec: GenerateSpec, rng: Rng): Item {
  const pool = ITEM_SLOTS.filter((s) => !spec.excludeSlots?.includes(s));
  const slot = spec.slot ?? (rng.pick(pool.length ? pool : ITEM_SLOTS) as ItemSlot);
  const rarity: Rarity = rng.chance(epicChance(spec.ilvl)) ? 'epic' : 'rare';
  let trinketEffectId: string | undefined;
  if (slot === 'trinket' && rng.chance(TRINKET_EFFECT_CHANCE)) trinketEffectId = rng.pick(trinketsForRole(spec.role))?.id;
  const priority = spec.classId ? CLASS_STAT_PROFILES[spec.classId]?.priority : undefined;
  return makeItem(
    {
      id: spec.id,
      slot,
      ilvl: spec.ilvl,
      rarity,
      ...(priority ? { priority } : {}),
      ...(trinketEffectId ? { trinketEffectId } : {}),
      ...(spec.source ? { source: spec.source } : {}),
    },
    rng,
  );
}

/** Fresh character: ilvl 200 in all 8 slots, the class's top two stats, no sockets. */
export function starterGear(classId: ClassId, priority: readonly SecondaryStat[], nextId: () => ItemId): Item[] {
  const p: [SecondaryStat, SecondaryStat] = [priority[0] ?? 'haste', priority[1] ?? 'crit'];
  void classId;
  return ITEM_SLOTS.map((slot) => ({
    id: nextId(),
    slot,
    ilvl: BASE_ILVL,
    rarity: 'common' as const,
    secondaries: p.map((stat) => ({ stat, rating: secondaryBudget(BASE_ILVL, slot, 'common') })),
    sockets: 0,
    gems: [],
    source: 'starter' as const,
  }));
}

// ============================================================================
// Valuation (bot auto-equip, "upgrade" arrows in the UI)
// ============================================================================

/** Value of 1% of a secondary, by priority position, in item-level points. */
export const PRIORITY_VALUE: readonly number[] = [0.6, 0.45, 0.3, 0.15];

/**
 * Rough item score in item-level points: ilvl dominates, secondaries break ties by class
 * priority, sockets/trinket effects/legendaries add a little. Gems are not counted (they move).
 */
export function itemScore(item: Item, classId?: ClassId): number {
  const priority = (classId && CLASS_STAT_PROFILES[classId]?.priority) || SECONDARY_STATS;
  let s = effectiveIlvl(item);
  for (const r of item.secondaries) {
    const i = priority.indexOf(r.stat);
    s += (r.rating / RATING_PER_PERCENT) * (PRIORITY_VALUE[i] ?? 0.1);
  }
  s += item.sockets * 2;
  if (item.trinketEffectId) s += 3;
  if (item.legendaryId) s += 15;
  if (classId && item.setId && item.setId === TIER_SET_IDS[classId]) s += TIER_PIECE_SCORE;
  return s;
}

/** True when `item` is valid gear: 2 distinct secondaries, gems match sockets, ranks in range. */
export function validateItem(item: Item): string[] {
  const errs: string[] = [];
  if (!ITEM_SLOTS.includes(item.slot)) errs.push(`${item.id}: bad slot ${item.slot}`);
  if (item.secondaries.length !== 2) errs.push(`${item.id}: needs 2 secondaries`);
  if (new Set(item.secondaries.map((s) => s.stat)).size !== item.secondaries.length) errs.push(`${item.id}: duplicate secondary`);
  if (item.sockets < 0 || item.sockets > MAX_SOCKETS) errs.push(`${item.id}: bad socket count`);
  if (item.gems.length !== item.sockets) errs.push(`${item.id}: gems/sockets mismatch`);
  for (const g of item.gems) if (g && (g.rank < 1 || g.rank > MAX_GEM_RANK)) errs.push(`${item.id}: bad gem rank ${g.rank}`);
  const mw = item.masterwork ?? 0;
  if (!Number.isInteger(mw) || mw < 0 || mw > MASTERWORK_MAX) errs.push(`${item.id}: bad masterwork ${mw}`);
  if (!RARITIES.includes(item.rarity)) errs.push(`${item.id}: bad rarity ${String(item.rarity)}`);
  for (const r of item.secondaries) if (r.quality !== undefined && !(r.quality >= 0.899 && r.quality <= 1.101)) errs.push(`${item.id}: bad quality ${r.quality}`);
  const f = item.forge;
  if (f && (f.steps < 0 || f.fails < 0 || f.resonance < 0 || f.resonance > 100 || f.invested < 0)) errs.push(`${item.id}: bad forge state`);
  return errs;
}
